import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { format, subDays } from "date-fns";
import toast from "react-hot-toast";

import { DateRangeFilter } from "../../components/Filters/DateRangeFilter";
import { EventCancellationListItem, formatAffectedInscriptions } from "../../models/eventCancellation";
import {
  useExportEventCancellationsMutation,
  useGetCancellationReasonsQuery,
  useGetAdminEventCancellationsQuery,
  useGetEventCancellationsSummaryQuery,
} from "../../store/services/event-cancellations";
import { useTenantScope } from "../../hooks/useTenantScope";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useTenantOptions } from "../../hooks/useTenantOptions";
import { BenefitsMetricsTenantScopeHeader } from "../Reporting/components/BenefitsMetricsWidgets";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { downloadBlob, buildAnalyticsExportFilename } from "../../utils/functions/downloadBlob";
import { formatDateWithoutSeconds } from "../../utils/functions/date";

const DEFAULT_PER_PAGE = 20;
const NOTE_PREVIEW_LENGTH = 60;

const toApiDate = (date: Date): string => format(date, "yyyy-MM-dd");

const truncate = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max)}…` : value;

const SummaryTile = ({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: ReactNode;
}) => (
  <div className="col-12 md:col-4">
    <div className="surface-ground border-1 surface-border border-round p-3 h-full">
      <div className="flex align-items-center gap-2 text-600 text-sm mb-2">
        <i className={`pi ${icon} text-primary`}></i>
        <span>{label}</span>
      </div>
      {children}
    </div>
  </div>
);

export const EventCancellationsHistory = () => {
  const scope = useTenantScope();
  const selectedTenantId = scope.tenantId;
  const { options: tenantOptions } = useTenantOptions();

  const [dateFrom, setDateFrom] = useState<Date | null>(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState<Date | null>(new Date());
  const [reasonIds, setReasonIds] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [selectedRow, setSelectedRow] = useState<EventCancellationListItem>();
  const [filtersTenantId, setFiltersTenantId] = useState(selectedTenantId);

  if (filtersTenantId !== selectedTenantId) {
    setFiltersTenantId(selectedTenantId);
    setPage(1);
    setReasonIds([]);
    setSelectedRow(undefined);
  }

  const { enabled: isCancellationEnabled, loading: isLoadingFlag } = useIsFeatureFlagEnabled(
    "admin_event_cancellation"
  );
  const isAllTenantsScope =
    !scope.loading && scope.role === "SUPER_ADMIN" && selectedTenantId === null;
  const canViewHistory = isAllTenantsScope || isCancellationEnabled;
  const isResolvingAccess = scope.loading || (!isAllTenantsScope && isLoadingFlag);

  const baseFilters = {
    tenantId: selectedTenantId,
    reasonIds,
    from: toApiDate(dateFrom ?? subDays(new Date(), 30)),
    to: toApiDate(dateTo ?? new Date()),
  };

  const filters = { ...baseFilters, page, perPage };
  const skipQueries = !canViewHistory || scope.loading;

  const { data: reasons } = useGetCancellationReasonsQuery(
    {
      tenantId: selectedTenantId,
      includeInactive: true,
      includeTenantCustomReasons: selectedTenantId === null,
    },
    { skip: skipQueries }
  );
  const { data: cancellations, isLoading } = useGetAdminEventCancellationsQuery(
    filters,
    { skip: skipQueries }
  );
  const { data: summary, isLoading: isLoadingSummary } = useGetEventCancellationsSummaryQuery(
    baseFilters,
    { skip: skipQueries }
  );
  const [exportCancellations, { isLoading: isExporting, reset }] =
    useExportEventCancellationsMutation();

  const tenantLabelById = useMemo(() => {
    const labels = new Map<string, string>();
    tenantOptions.forEach((tenant) => labels.set(tenant.value, tenant.label));
    return labels;
  }, [tenantOptions]);

  const reasonOptions = useMemo(
    () =>
      (reasons ?? []).map((reason) => ({
        label: reason.tenantId
          ? `${reason.reasonText} (${tenantLabelById.get(reason.tenantId) ?? reason.tenantId})`
          : reason.reasonText,
        value: reason.id,
      })),
    [reasons, tenantLabelById]
  );

  const hasActiveFilters = reasonIds.length > 0;

  const handleExport = async () => {
    try {
      const result = await exportCancellations(baseFilters).unwrap();
      if (result.status === 204) {
        toast("No hay cancelaciones para los filtros seleccionados", { icon: "ℹ️" });
        return;
      }
      downloadBlob(
        result.blob,
        buildAnalyticsExportFilename(result.filename, tenantOptions, baseFilters)
      );
      toast.success("CSV exportado");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) ?? "Error al exportar el CSV");
    } finally {
      reset();
    }
  };

  const handlePage = (e: DataTablePageEvent) => {
    setPage((e.page ?? 0) + 1);
    setPerPage(e.rows);
  };

  const noteTemplate = (row: EventCancellationListItem) =>
    row.internalNote ? (
      <span title={row.internalNote}>{truncate(row.internalNote, NOTE_PREVIEW_LENGTH)}</span>
    ) : (
      <span className="text-500">—</span>
    );

  const refundTemplate = (row: EventCancellationListItem) => (
    <Tag
      severity={row.requiresRefund ? "danger" : "secondary"}
      value={row.requiresRefund ? "Sí" : "No"}
    />
  );

  const affectedTemplate = (row: EventCancellationListItem) =>
    row.affectedUsers + row.affectedCompanions;

  const detailTemplate = (row: EventCancellationListItem) => (
    <Button
      icon="pi pi-eye"
      rounded
      outlined
      severity="info"
      size="small"
      tooltip="Ver detalle"
      tooltipOptions={{ position: "top" }}
      onClick={() => setSelectedRow(row)}
    />
  );

  const header = (
    <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
      <h1 className="text-center m-0">Historial de cancelaciones</h1>
    </div>
  );

  if (isResolvingAccess) {
    return (
      <div className="p-card gap-4 p-3">
        {header}
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <ProgressSpinner />
        </div>
      </div>
    );
  }

  if (!canViewHistory) {
    return (
      <div className="p-card gap-4 p-3">
        {header}
        <div className="flex justify-content-center p-4">
          <Message severity="info" text="La sección está deshabilitada para este tenant." />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-card gap-4 p-3">
        {header}

        <BenefitsMetricsTenantScopeHeader tenantId={selectedTenantId} />

        <div className="flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
          <div className="flex flex-wrap align-items-end gap-3">
            <div className="flex flex-column gap-2">
              <label htmlFor="reasons" className="text-600 text-sm">
                Motivo
              </label>
              <MultiSelect
                inputId="reasons"
                value={reasonIds}
                options={reasonOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Todos los motivos"
                maxSelectedLabels={2}
                selectedItemsLabel="{0} motivos"
                showClear
                filter
                style={{ minWidth: "16rem", maxWidth: "22rem" }}
                onChange={(e) => {
                  setReasonIds(e.value ?? []);
                  setPage(1);
                }}
              />
            </div>
            <DateRangeFilter
              variant="stacked"
              columnClassName="flex flex-column gap-2"
              labelClassName="text-600 text-sm"
              labelFrom="Desde"
              labelTo="Hasta"
              calendarStyle={{ width: "11rem" }}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={(date) => {
                setDateFrom(date);
                setPage(1);
              }}
              onDateToChange={(date) => {
                setDateTo(date);
                setPage(1);
              }}
              disabled={isLoading}
            />
          </div>

          <Button
            label="Exportar CSV"
            icon="pi pi-download"
            outlined
            loading={isExporting}
            onClick={handleExport}
          />
        </div>

        <div className="grid mb-4">
          <SummaryTile icon="pi-ban" label="Total cancelaciones">
            <span className="text-3xl font-bold text-900">
              {isLoadingSummary ? "—" : (summary?.totalCancellations ?? 0)}
            </span>
          </SummaryTile>
          <SummaryTile icon="pi-chart-bar" label="Motivo más frecuente">
            <span className="text-xl font-bold text-900 line-height-3 block">
              {isLoadingSummary ? "—" : (summary?.topReasonText ?? "—")}
            </span>
            {!!summary?.topReasonCount && (
              <span className="block text-500 text-sm mt-1">
                {summary.topReasonCount}{" "}
                {summary.topReasonCount === 1 ? "cancelación" : "cancelaciones"}
              </span>
            )}
          </SummaryTile>
          <SummaryTile icon="pi-users" label="Usuarios afectados">
            <span className="text-3xl font-bold text-900">
              {isLoadingSummary ? "—" : (summary?.affectedUsers ?? 0)}
            </span>
          </SummaryTile>
        </div>

        <DataTable
          key={selectedTenantId ?? "all"}
          value={cancellations?.items ?? []}
          loading={isLoading}
          dataKey="id"
          lazy
          paginator
          rows={perPage}
          first={(page - 1) * perPage}
          totalRecords={cancellations?.total ?? 0}
          rowsPerPageOptions={[10, 20, 50]}
          onPage={handlePage}
          tableStyle={{ minWidth: "70rem" }}
          responsiveLayout="scroll"
          emptyMessage={
            hasActiveFilters
              ? "No hay cancelaciones que coincidan con los filtros."
              : "No hay cancelaciones en el período seleccionado."
          }
        >
          <Column
            header="Fecha"
            body={(row: EventCancellationListItem) => formatDateWithoutSeconds(row.cancelledAt)}
            style={{ width: "11rem" }}
          />
          <Column
            header="Evento"
            body={(row: EventCancellationListItem) => (
              <Link to={`/events/${row.eventId}`} className="font-medium">
                {row.eventName}
              </Link>
            )}
            style={{ minWidth: "16rem" }}
          />
          <Column field="reasonText" header="Motivo" style={{ width: "14rem" }} />
          <Column header="Observación" body={noteTemplate} style={{ minWidth: "14rem" }} />
          <Column
            header="Inscriptos afectados"
            body={affectedTemplate}
            style={{ width: "10rem" }}
          />
          <Column field="cancelledByName" header="Cancelado por" style={{ width: "12rem" }} />
          <Column
            header="Requiere reembolso"
            body={refundTemplate}
            style={{ width: "9rem" }}
          />
          <Column header="Detalle" body={detailTemplate} style={{ width: "7rem" }} />
        </DataTable>
      </div>

      <Dialog
        visible={!!selectedRow}
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-ban text-primary"></i>
            <span>Detalle de la cancelación</span>
          </div>
        }
        modal
        style={{ width: "40rem", maxWidth: "95vw", maxHeight: "90vh" }}
        breakpoints={{ "960px": "95vw" }}
        contentStyle={{ overflowY: "auto" }}
        onHide={() => setSelectedRow(undefined)}
      >
        {selectedRow && (
          <div className="flex flex-column gap-3">
            <div>
              <small className="text-600 block">Evento</small>
              <Link to={`/events/${selectedRow.eventId}`} className="font-medium">
                {selectedRow.eventName}
              </Link>
              <span className="text-500"> (ID #{selectedRow.eventId})</span>
            </div>

            <div className="flex flex-wrap gap-4">
              <div>
                <small className="text-600 block">Fecha y hora</small>
                <span className="font-medium">
                  {formatDateWithoutSeconds(selectedRow.cancelledAt)}
                </span>
              </div>
              <div>
                <small className="text-600 block">Cancelado por</small>
                <span className="font-medium">{selectedRow.cancelledByName}</span>
              </div>
              <div>
                <small className="text-600 block">Motivo</small>
                <span className="font-medium">{selectedRow.reasonText}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <div>
                <small className="text-600 block">Inscriptos afectados</small>
                <span className="font-medium">
                  {formatAffectedInscriptions(
                    selectedRow.affectedUsers,
                    selectedRow.affectedCompanions
                  )}
                </span>
              </div>
              <div>
                <small className="text-600 block mb-1">Requiere reembolso</small>
                {refundTemplate(selectedRow)}
              </div>
            </div>

            <div>
              <small className="text-600 block mb-2">Observación interna</small>
              <div
                className="p-3 border-1 surface-border border-round surface-ground"
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {selectedRow.internalNote || "—"}
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};
